import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '@/services/axios'
import { NotificationService } from '@/services/NotificationService'
import { PushNotificationService } from '@/services/PushNotificationService'
import { useAuthStore } from '@/stores/authStore'
import { storeToRefs } from 'pinia'

export function useNotifications() {
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)
  const notifications = ref([])
  const unreadCount = ref(0)
  const isLoading = ref(false)

  const isAuthenticated = computed(() => {
    return user.value != null
  })

  const userId = computed(() => {
    return user.value?.id
  })

  // const unreadCount = computed(() => {
  //   return Array.isArray(notifications.value)
  //     ? notifications.value.filter((n) => !n.read_at).length
  //     : 0
  // })

  const fetchNotifications = async () => {
    if (!isAuthenticated.value) return

    isLoading.value = true
    try {
      const response = await api.get('/api/notifications')
      notifications.value = response.data.notifications.data
      unreadCount.value = response.data.unreadCount
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      isLoading.value = false
    }
  }

  const markAsRead = async (notificationId) => {
    if (!isAuthenticated.value) return

    try {
      await api.post(`/api/notifications/${notificationId}/mark-as-read`)
      // Update local state
      notifications.value = notifications.value.map((n) =>
        n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n,
      )
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    if (!isAuthenticated.value) return

    try {
      await api.put('/api/notifications/mark-all-as-read')
      // Update local state
      notifications.value = notifications.value.map((n) => ({
        ...n,
        read_at: new Date().toISOString(),
      }))
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  // Trigger a push notification
  const triggerPushNotification = async (notification) => {}

  // Set up Echo listener immediately when the composable is imported
  // This ensures we don't miss any notifications
  const setupNotificationListener = () => {
    const echo = window.Echo

    if (isAuthenticated.value && echo && userId.value) {

      // Listen for new notifications
      echo.private(`App.Models.User.${userId.value}`).notification(async (notification) => {

        if (!Array.isArray(notifications.value)) {
          notifications.value = []
        }

        // Check if this notification already exists in our array by ID
        const existingIndex = notifications.value.findIndex((n) => n.id === notification.id)

        if (existingIndex >= 0) {
          // Update existing notification if needed
        } else {
          // Only fetch notifications from the server to ensure we have the correct state
          await fetchNotifications()
        }

        // Show browser notification
        try {
          await NotificationService.showNotification(notification.title || 'New Notification', {
            body: notification.message,
            url: `/notifications/${notification.id}`,
          })

          // After showing the browser notification, trigger a push notification
          // await triggerPushNotification(notification);
        } catch (error) {
          console.error('Error showing browser notification:', error)
        }
      })
    }
  }

  const cleanupNotificationListener = () => {
    const echo = window.Echo
    if (isAuthenticated.value && echo && userId.value) {
      echo.leave(`App.Models.User.${userId.value}`)
    }
  }

  // Set up the notification listener immediately when the composable is imported
  // This is outside of onMounted to ensure we don't miss any notifications
  if (typeof window !== 'undefined') {
    // Make sure we're in a browser environment
    const initializeNotifications = () => {
      if (isAuthenticated.value) {
        fetchNotifications()
        setupNotificationListener()
      }
    }

    // Try to set up immediately
    initializeNotifications()

    // Also set up during component mounting as a fallback
    onMounted(initializeNotifications)
  } else {
    // Standard initialization for server-side rendering
    onMounted(() => {
      if (isAuthenticated.value) {
        fetchNotifications()
        setupNotificationListener()
      }
    })
  }

  onUnmounted(() => {
    cleanupNotificationListener()
  })

  return {
    notifications,
    isLoading,
    unreadCount,
    isAuthenticated,
    userId,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    setupNotificationListener,
    cleanupNotificationListener,
    triggerPushNotification,
  }
}
